const ADMINISTRATIVE_AREA_TYPES = new Set(["city", "district_type", "neighborhood"]);

const GEOMETRY_OPERATION_INTERACTION_NAMES = [
    "addGeometry",
    "addGeometry_point",
    "addGeometry_line",
    "addGeometry_circle",
    "cutGeometry",
    "measurumentGeometryIntreaction",
    "boxGeometryInteraction",
    "isochronePointInteraction",
    "addPoiInteraction",
    "draw_modify_interaction",
    "edit_interaction",
    "selectTranslate",
    "transformTranslate",
    "selectRotate",
    "transformRotate"
];

const getMapInstance = (mapTool) => mapTool?.getMap?.() || mapTool?.map;

const getLayerSource = (mapTool, layerName) => {
    try {
        return mapTool?.getLayerSource?.(layerName)
            || mapTool?.findLayer?.(layerName)?.getSource?.()
            || null;
    } catch {
        return null;
    }
};

const removeFeatureSafely = (source, feature) => {
    if (!source?.removeFeature || !feature) return;

    try {
        source.removeFeature(feature);
    } catch {
        // Cleanup must never break the geometry operation that just finished.
    }
};

const clearSourceSafely = (source) => {
    if (!source?.clear) return;

    try {
        source.clear(true);
    } catch {
        source.clear();
    }
};

const removeMatchingFeatures = (source, predicate) => {
    if (!source?.getFeatures || typeof predicate !== "function") return;

    source.getFeatures()
        .filter(predicate)
        .forEach(feature => removeFeatureSafely(source, feature));
};

const disableInteractionSafely = (mapTool, interactionName) => {
    if (!mapTool || !interactionName) return;

    try {
        const interaction = mapTool.getInteraction?.(interactionName);
        interaction?.setActive?.(false);
    } catch {
        // Ignore stale interaction references.
    }

    try {
        mapTool.disableInteraction?.(interactionName);
    } catch {
        // Ignore map-tool implementations that do not track this interaction.
    }
};

export const cleanupGeometryOperationArtifacts = (mapTool, options = {}) => {
    const mapInstance = getMapInstance(mapTool);
    if (!mapInstance) return;

    const {
        clearAdministrativeDrawFeatures = true,
        clearDrawVectorLayer = true,
        clearCutCopyLayer = true,
        clearSnapFeatures = true,
        disableInteractions = true,
        render = true
    } = options;

    if (disableInteractions) {
        GEOMETRY_OPERATION_INTERACTION_NAMES.forEach(interactionName => {
            disableInteractionSafely(mapTool, interactionName);
        });
    }

    if (clearSnapFeatures) {
        const workspaceSource = getLayerSource(mapTool, "workspace-layer");
        // Snap helpers live in workspace-layer but are not user geometry.
        removeMatchingFeatures(workspaceSource, feature => feature?.get?.("is_snap") === true);
        mapTool?.findLayer?.("workspace-layer")?.changed?.();
    }

    if (clearAdministrativeDrawFeatures) {
        const drawSource = getLayerSource(mapTool, "draw-layer");
        removeMatchingFeatures(drawSource, feature => ADMINISTRATIVE_AREA_TYPES.has(feature?.get?.("areaType")));
        mapTool?.findLayer?.("draw-layer")?.changed?.();
    }

    if (clearDrawVectorLayer) {
        clearSourceSafely(getLayerSource(mapTool, "draw-layer_vector"));
        mapTool?.findLayer?.("draw-layer_vector")?.changed?.();
    }

    if (clearCutCopyLayer) {
        clearSourceSafely(getLayerSource(mapTool, "cutcopy-layer"));
        mapTool?.findLayer?.("cutcopy-layer")?.changed?.();
    }

    if (render) {
        mapInstance.renderSync?.();
        mapInstance.render?.();
    }
};
